import { FetchFunction } from "../components/FetchProvider";
import descriptors, { ResourceName } from "../descriptors";
import { ResourceSchema } from "../types";
import { parse, ParsedQs, stringify } from "qs";
import { ResourceDescriptor } from "../descriptors/common";

function createQueryWithQS(query: Record<string, any>) {
    return stringify(query, { addQueryPrefix: true });
}

abstract class ResourceRequestBase {
    public readonly resourceName: string;
    public readonly resourceId: number;
    private readonly data: any;
    private readonly method: string;
    public readonly query: Record<string, any>;

    constructor(
        method: string,
        resourceName: string,
        resourceId: number = null,
        data: any = null,
        query: Record<string, any> = null,
    ) {
        this.resourceName = resourceName;
        this.resourceId = resourceId;
        this.data = data;
        this.method = method;
        this.query = query;
    }

    getData() {
        return this.data;
    }

    getMethod() {
        return this.method;
    }

    getURL() {
        const url = this.resourceId ? `/api/${this.resourceName}/${this.resourceId}` : `/api/${this.resourceName}/`;
        if (!this.query) return url;

        return url + createQueryWithQS(this.query);
    }
}

export class ListRequest extends ResourceRequestBase {
    constructor(resourceName: string, query: Record<string, any> = null) {
        super("GET", resourceName, null, null, query);
    }
}

export class ReadRequest extends ResourceRequestBase {
    constructor(resourceName: string, resourceId: number, expand: any[] = []) {
        super("GET", resourceName, resourceId, null, {
            expand: expand,
        });
    }
}

export class CreateRequest extends ResourceRequestBase {
    constructor(resourceName: string, data: any) {
        super("POST", resourceName, null, data);
    }
}

export class UpdateRequest extends ResourceRequestBase {
    constructor(resourceName: string, data: any, resourceId: number, method: "PUT" | "PATCH" = "PUT") {
        super(method, resourceName, resourceId, data);
    }
}

export class DeleteRequest extends ResourceRequestBase {
    constructor(resource: string, resourceId: number) {
        super("DELETE", resource, resourceId);
    }
}

export class BatchResourceRequest {
    // initialized externally
    private static requestCounter = 0;

    private requests: ResourceRequestBase[];
    private __atomic: boolean;

    constructor(...requests: ResourceRequestBase[]) {
        this.requests = requests;
        this.__atomic = false;
    }

    get atomic() {
        return this.__atomic;
    }

    set atomic(val) {
        this.__atomic = val;
    }

    getURL(): string {
        return "/api/batch";
    }

    getMethod() {
        return "POST";
    }

    getData() {
        let data = { requests: [], atomic: this.__atomic };
        this.requests.forEach((request) => {
            data.requests.push({
                method: request.getMethod(),
                resourceName: request.resourceName,
                resourceId: request.resourceId,
                query: request.query,
                body: request.getData(),
                requestId: `batch-request-${++BatchResourceRequest.requestCounter}`,
            });
        });
        return data;
    }
}

/// helper for the specific fetchers
async function fetchBase(
    fetch: FetchFunction,
    request: ListRequest | BatchResourceRequest | ReadRequest,
): Promise<any> {
    const method = request.getMethod();
    const data = request.getData();

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    const response = await fetch(request.getURL(), {
        method: method,
        headers: headers,
        body: data ? JSON.stringify(data) : null,
    });
    return response;
}

/// the result of a Gauss list request
export interface ListResult {
    count: number; /// the count of rows returned
    rows: any[]; /// the returned data
    resource: string; /// the name of the resource
}

/// Async function to fetch a list request
export async function fetchList(fetch: FetchFunction, request: ListRequest): Promise<ListResult> {
    const response = await fetchBase(fetch, request);

    const responseData = await response.json();

    if (response.ok) {
        try {
            return {
                count: responseData.data.count,
                rows: responseData.data.rows,
                resource: responseData.resourceName,
            };
        } catch (e) {
            console.error("Error: %o", e);
            return null;
        }
    }
}

/// async function that batches all the list requests and returns the result in the same order
export async function fetchLists(fetch: FetchFunction, requests: ListRequest[]): Promise<ListResult[]> {
    const batch = new BatchResourceRequest(...requests);
    batch.atomic = true;
    const response = await fetchBase(fetch, batch);

    const responseData = await response.json();

    if (response.ok) {
        try {
            const result: ListResult[] = [];
            for (const resp of responseData.data.responses) {
                const body = resp.body;
                if (body.status !== "success") {
                    console.error("Error with one of the responses in the batch: %o", body);
                    return null;
                }

                result.push({
                    count: body.data.count,
                    rows: body.data.rows,
                    resource: body.resourceName,
                });
            }
            return result;
        } catch (e) {
            console.error("Error: %o", e);
            return null;
        }
    }
}

function createQueryObject(url: URL, desc: ResourceDescriptor, schema: ResourceSchema) {
    const params = parse(url.search, { ignoreQueryPrefix: true });

    let limit: number = "rpp" in params ? parseInt(params.rpp as string) : desc.listDefaults.limit;
    let offset: number = "page" in params ? (parseInt(params.page as string) - 1) * limit : desc.listDefaults.offset;
    let sort = Object.assign({}, desc.listDefaults.sort);
    let expand = desc.expand ? desc.expand.list || [] : [];
    let filters = {};

    if ("sort" in params) {
        const sortP = params.sort as ParsedQs;
        if ("name" in sortP) sort.name = sortP.name.toString();
        if ("direction" in sortP) sort.direction = sortP.direction as "asc" | "desc";
    }

    if ("filters" in params) {
        filters = Object.assign({}, params.filters);
    }

    if (!(sort.name in schema)) throw new Error(`Unknown sorting name '${sort.name}'`);

    if (["asc", "desc"].indexOf(sort.direction) == -1) throw new Error(`Unknown sorting direction '${sort.direction}'`);

    if (offset < 0) offset = 0;

    return {
        limit: limit,
        offset: offset,
        sort: sort,
        filters: filters,
        expand: expand,
    };
}

/**
 * React router loader to fetch a paginated list for a resource.
 *
 * It handles query filters and pagination from the URL.
 */
export async function crudListLoader(
    fetch: FetchFunction,
    request: Request,
    resourceName: ResourceName,
): Promise<ListResult> {
    const descriptor = descriptors[resourceName];
    return await fetchList(
        fetch,
        new ListRequest(
            resourceName,
            createQueryObject(new URL(request.url), descriptor, window.schemas.resources[resourceName]),
        ),
    );
}

function* getRelationFields(schema: ResourceSchema) {
    for (let [_, field] of Object.entries(schema) as any) {
        if (field.type.name == "relation") yield field;
    }
}

/// read a resource by id
export async function fetchRead(
    fetch: FetchFunction,
    resourceName: ResourceName,
    id: number,
    useExpandFromDescriptor: boolean = true,
): Promise<any> {
    const descriptor = descriptors[resourceName];
    const schema = window.schemas.resources[resourceName];
    let expand: string[] = [];
    if (useExpandFromDescriptor) {
        expand = descriptor.expand?.read ? [...descriptor.expand.read] : [];
    }

    for (const field of getRelationFields(schema)) expand.push(field.name);

    const request = new ReadRequest(resourceName, id, expand);
    const response = await fetchBase(fetch, request);
    if (response.ok) {
        try {
            const responseData = await response.json();
            return responseData.data;
        } catch (e) {
            console.error("Error: %o", e);
            return null;
        }
    }
}

export async function submitDelete(fetch: FetchFunction, resourceName: ResourceName, id: number): Promise<void> {
    const headers = new Headers();
    headers.append("Accept", "application/json");
    const response = await fetch(`/api/${resourceName}/${id}`, {
        method: "DELETE",
        headers: headers,
    });

    if (!response.ok) {
        throw new Error("Failed to delete resource");
    }
}

export async function submitBatch(fetch: FetchFunction, requests: (CreateRequest | UpdateRequest)[]): Promise<void> {
    const batch = new BatchResourceRequest(...requests);
    const response = await fetchBase(fetch, batch);

    const responseData = await response.json();

    if (!response.ok) {
        throw new Error(`Failed to delete resource: ${responseData}`);
    }

    try {
        for (const resp of responseData.data.responses) {
            const body = resp.body;
            if (body.status !== "success") {
                throw new Error(`Submit failed: ${body}`);
            }
        }
    } catch (e) {
        throw new Error(`Error checking submit response: ${responseData}`);
    }
}
